import { useEffect, useRef } from "react";
import { TextInput, Stack, Switch, Text } from "@mantine/core";
import { useForm } from "@mantine/form";
import { RichTextEditor } from "@mantine/tiptap";
import { useEditor } from "@tiptap/react";
import { Abbreviation } from "../../../services/admin/abbreviations.service";
import { createClinicalRichTextExtensions } from "../../shared/tiptap/clinicalRichTextExtensions";
import {
  CLINICAL_RICH_TEXT_COLORS,
  CLINICAL_RICH_TEXT_PARSE_OPTIONS,
} from "../../../utils/clinicalRichText";

const SHORTCODE_REGEX = /^`{0,1}[A-Za-z0-9]{1,50}$/;

export interface AbbreviationFormValues {
  shortcode: string;
  expansion: string;
  isActive: boolean;
}

interface AbbreviationFormProps {
  initialValues?: Abbreviation;
  onSubmit: (values: AbbreviationFormValues) => void;
}

export default function AbbreviationForm({
  initialValues,
  onSubmit,
}: AbbreviationFormProps) {
  const expansionRef = useRef<string>(initialValues?.expansion || "");

  const form = useForm<AbbreviationFormValues>({
    initialValues: {
      shortcode: initialValues?.shortcode || "",
      expansion: initialValues?.expansion || "",
      isActive: initialValues?.isActive ?? true,
    },
    validate: {
      shortcode: (value) => {
        if (!value.trim()) return "Shortcode is required";
        if (!SHORTCODE_REGEX.test(value.trim()))
          return "Must be an optional backtick followed by 1-50 alphanumeric characters";
        return null;
      },
      expansion: (value) => {
        const text = value.replace(/<[^>]*>/g, "").trim();
        if (!text) return "Expansion text is required";
        return null;
      },
    },
  });

  const editor = useEditor({
    extensions: createClinicalRichTextExtensions("Enter expansion text..."),
    content: initialValues?.expansion || "",
    parseOptions: CLINICAL_RICH_TEXT_PARSE_OPTIONS,
    onUpdate: ({ editor: ed }) => {
      const html = ed.getHTML();
      expansionRef.current = html;
      form.setFieldValue("expansion", html);
    },
  });

  useEffect(() => {
    if (editor && initialValues?.expansion) {
      const current = editor.getHTML();
      if (current !== initialValues.expansion) {
        editor.commands.setContent(initialValues.expansion, {
          parseOptions: CLINICAL_RICH_TEXT_PARSE_OPTIONS,
        });
      }
    }
  }, [editor, initialValues?.expansion]);

  return (
    <form
      noValidate
      onSubmit={form.onSubmit((values) => {
        onSubmit({ ...values, expansion: expansionRef.current });
      })}
    >
      <Stack gap="md">
        <TextInput
          label="Shortcode"
          placeholder="e.g., `IBD or IBD"
          required
          {...form.getInputProps("shortcode")}
        />

        <div>
          <Text component="div" size="sm" fw={500} mb={4}>
            Expansion Text <span style={{ color: "red" }}>*</span>
          </Text>
          <RichTextEditor
            editor={editor}
            styles={{
              root: {
                border: form.errors.expansion
                  ? "1px solid var(--mantine-color-red-6)"
                  : "1px solid var(--mantine-color-gray-4)",
                borderRadius: "var(--mantine-radius-sm)",
              },
              content: { minHeight: 120 },
            }}
          >
            <RichTextEditor.Toolbar>
              <RichTextEditor.ControlsGroup>
                <RichTextEditor.Bold />
                <RichTextEditor.Italic />
                <RichTextEditor.Underline />
              </RichTextEditor.ControlsGroup>
              <RichTextEditor.ControlsGroup>
                <RichTextEditor.BulletList />
                <RichTextEditor.OrderedList />
              </RichTextEditor.ControlsGroup>
              <RichTextEditor.ControlsGroup>
                <RichTextEditor.ColorPicker
                  colors={CLINICAL_RICH_TEXT_COLORS}
                />
                <RichTextEditor.UnsetColor />
                <RichTextEditor.Highlight />
              </RichTextEditor.ControlsGroup>
            </RichTextEditor.Toolbar>
            <RichTextEditor.Content />
          </RichTextEditor>
          {form.errors.expansion ? (
            <div
              style={{
                color: "var(--mantine-color-red-6)",
                fontSize: "var(--mantine-font-size-xs)",
                marginTop: 4,
              }}
            >
              {form.errors.expansion}
            </div>
          ) : (
            <Text size="xs" c="dimmed" mt={4}>
              Tip: Use <b>??</b> to create fill-in fields. Each <b>??</b>{" "}
              becomes an input the user can type into when the abbreviation is
              expanded.
            </Text>
          )}
        </div>

        <Switch
          label="Active"
          {...form.getInputProps("isActive", { type: "checkbox" })}
        />
      </Stack>
    </form>
  );
}
